// Kiểu dữ liệu + nhãn của mục Cài đặt › Nhân viên (API /settings/members — docs/KE_HOACH_THANH_VIEN_DON_VI.md).
// Thuần, không import React — test ở tests/unit/web-members.test.ts.

export interface MemberRow {
  userId: string
  isOwner: boolean
  isMe: boolean
  name: string | null
  phone: string | null
  email: string | null
  roleId: string | null
  roleName: string | null
  status: 'active' | 'suspended'
  mustChangePassword: boolean
  lastLoginAt: string | null
  addedAt: string
  canResetPassword: boolean
}

export interface UnitRoleRow {
  id: string
  name: string
  permissions: string[]
  memberCount: number
}

export interface MembersResponse {
  unit: { id: string; name: string; tenantType: string }
  grantable: string[]
  limits: { maxStaff: number; maxRoles: number; passwordMin: number }
  members: MemberRow[]
  roles: UnitRoleRow[]
}

export interface ActivityItem {
  id: string
  at: string
  userId: string | null
  name: string | null
  /** "POST /orders", "PATCH /orders/:id"… */
  action: string
  target: string | null
}

/** Thao tác của nhân viên → câu dễ hiểu ("Lên đơn", "Sửa đơn"…). Thao tác lạ → "Thao tác khác". */
const ACTION_LABEL: Record<string, string> = {
  'POST /orders': 'Lên đơn',
  'POST /orders/batch-import': 'Lên đơn bằng Excel',
  'POST /orders/:id/cancel': 'Hủy đơn',
  'PATCH /orders/:id': 'Sửa đơn',
  'POST /orders/sagas/:id/retry': 'Tạo lại vận đơn',
  'POST /intake/:id/accept': 'Lên đơn từ tin nhắn',
  'POST /intake': 'Lưu tin nhắn đặt hàng',
  'POST /intake/:id/reject': 'Bỏ tin nhắn đặt hàng',
  'POST /ship-payments/batches': 'Gộp lệnh chuyển tiền ship',
  'POST /ship-payments/batches/:id/cancel': 'Bỏ gộp lệnh tiền ship',
  'POST /shipping/print': 'In vận đơn',
  'POST /shipping/waybills/download': 'In / tải vận đơn',
  'POST /products': 'Thêm sản phẩm',
  'PATCH /products/:id': 'Sửa sản phẩm',
  'DELETE /products/:id': 'Ngừng bán sản phẩm',
  'POST /products/batch-import': 'Nhập sản phẩm từ file',
  'POST /products/upload-image': 'Tải ảnh sản phẩm',
  'POST /suppliers': 'Thêm nhà cung cấp',
  'DELETE /suppliers/:id': 'Xóa nhà cung cấp',
  'POST /suppliers/:id/choose': 'Chọn nhà cung cấp',
  'POST /customers': 'Thêm khách hàng',
  'PATCH /customers/:id': 'Sửa khách hàng',
  'POST /customers/:id/addresses': 'Thêm địa chỉ khách',
  'PATCH /customers/:id/addresses/:addressId': 'Sửa địa chỉ khách',
  'DELETE /customers/:id/addresses/:addressId': 'Xóa địa chỉ khách',
  'POST /rescue': 'Gợi ý cứu đơn',
  'POST /rescue/:orderId/approve': 'Duyệt cứu đơn',
  'POST /rescue/:orderId/reject': 'Bỏ cứu đơn',
  'DELETE /rescue/:orderId': 'Xóa gợi ý cứu đơn',
  'POST /reconcile/return-claims': 'Báo thiếu / hỏng hàng hoàn',
  'POST /settings/warehouses': 'Thêm kho lấy hàng',
  'PUT /settings/warehouses/:id': 'Sửa kho lấy hàng',
  'POST /settings/warehouses/:id/default': 'Đặt kho mặc định',
  'POST /settings/warehouses/:id/deactivate': 'Ngừng dùng kho',
  'POST /network/units': 'Tạo cấp dưới',
  'PUT /network/referral': 'Đổi bảng giá link giới thiệu',
  'POST /network/invites': 'Tạo link giới thiệu',
  'POST /network/invites/:id/revoke': 'Thu hồi link giới thiệu',
  'POST /price-lists/library': 'Tạo bảng giá',
  'PUT /price-lists/library/:id': 'Sửa bảng giá',
  'POST /price-lists/library/:id/archive': 'Lưu trữ bảng giá',
  'POST /price-lists/library/:id/move-units': 'Chuyển đơn vị sang bảng giá khác',
  'POST /price-lists/assign': 'Gán bảng giá cho cấp dưới',
  'POST /commissions/schemes': 'Tạo bảng chiết khấu',
  'PUT /commissions/schemes/:id': 'Sửa bảng chiết khấu',
  'POST /commissions/schemes/:id/archive': 'Lưu trữ bảng chiết khấu',
  'POST /commissions/assign': 'Gán bảng chiết khấu cho CTV',
  'POST /imports/columns': 'Đọc cột file nhập',
}

export function staffActionLabel(action: string): string {
  return ACTION_LABEL[action] ?? 'Thao tác khác'
}

/** Thao tác có đích là một đơn → chi tiết đơn mở được. */
export function activityOrderLink(action: string, target: string | null): string | null {
  if (!target) return null
  return /^(POST \/orders|POST \/orders\/batch-import|POST \/orders\/:id\/cancel|PATCH \/orders\/:id|POST \/intake\/:id\/accept|POST \/rescue\/:orderId\/(approve|reject))$/.test(action)
    ? `/orders/${encodeURIComponent(target)}`
    : null
}

/**
 * Mật khẩu tạm ngẫu nhiên cho nhân viên: 10 ký tự, có chữ hoa / thường / số, bỏ ký tự dễ nhầm (0 O 1 l I).
 * `random(n)` trả số nguyên [0, n) — trình duyệt dùng crypto.getRandomValues.
 */
export function makeTempPassword(random: (n: number) => number): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghijkmnopqrstuvwxyz'
  const digits = '23456789'
  const all = upper + lower + digits
  const pick = (set: string) => set.charAt(random(set.length))
  const chars = [pick(upper), pick(lower), pick(digits)]
  while (chars.length < 10) chars.push(pick(all))
  for (let i = chars.length - 1; i > 0; i--) {
    const j = random(i + 1)
    ;[chars[i], chars[j]] = [chars[j]!, chars[i]!]
  }
  return chars.join('')
}

export function browserRandom(n: number): number {
  const buf = new Uint32Array(1)
  // Bỏ phần dư để mọi số có xác suất như nhau
  const limit = Math.floor(0x1_0000_0000 / n) * n
  do crypto.getRandomValues(buf)
  while (buf[0]! >= limit)
  return buf[0]! % n
}
